import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useScrolled } from "@/hooks/useScrolled";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES, LANGUAGE_LABELS, translations } from "@/i18n/translations";
import GoogleTranslate from "@/components/GoogleTranslate";
import logo from "@/assets/hadassah-logo.png";

/* ─── Types ─── */
interface NavItem {
  label: string;
  href: string;
  children?: NavItem[];
}

/* ─── Desktop Dropdown ─── */
const NavDropdown = ({
  item,
  isTransparent,
}: {
  item: NavItem;
  isTransparent: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const panelId = `nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 120);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
    }

    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const isActive =
    location.pathname === item.href ||
    item.children?.some((c) => location.pathname === c.href);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={() => setOpen(!open)}
        onKeyDown={handleTriggerKeyDown}
        className={`group flex items-center gap-1.5 font-body text-[13px] font-medium uppercase tracking-[0.08em] transition-colors duration-300 ${
          isActive ? "text-accent" : "text-white/90 hover:text-white"
        }`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="menu"
      >
        {item.label}
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown panel */}
      <div
        id={panelId}
        role="menu"
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-250 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="min-w-[240px] bg-white/95 backdrop-blur-xl border border-border/40 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.15)] py-3 px-2 rounded-sm">
          {item.children!.map((child) => {
            const childActive = location.pathname === child.href;
            return (
              <Link
                key={child.href}
                to={child.href}
                onClick={() => setOpen(false)}
                role="menuitem"
                aria-current={childActive ? "page" : undefined}
                tabIndex={open ? 0 : -1}
                className={`flex cursor-pointer items-center gap-2.5 px-4 py-3 font-body text-[12px] uppercase tracking-[0.08em] rounded-sm transition-all duration-200 group/item focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                  childActive
                    ? "text-accent font-semibold bg-accent/5"
                    : "text-foreground/60 hover:text-foreground hover:bg-muted/50 hover:pl-5"
                }`}
              >
                <span className={`w-1 h-1 rounded-full transition-all duration-200 ${childActive ? "bg-accent" : "bg-transparent group-hover/item:bg-accent/50"}`} />
                {child.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─── Mobile Accordion Item ─── */
const MobileAccordion = ({
  item,
  onClose,
}: {
  item: NavItem;
  onClose: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const isParentActive =
    location.pathname === item.href ||
    item.children?.some((child) => location.pathname === child.href);
  const panelId = `mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;

  if (!item.children) {
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={`block border-b border-white/15 py-4 font-body text-[14px] font-medium uppercase tracking-[0.06em] transition-colors ${
          location.pathname === item.href
            ? "text-[#C9A95C]"
            : "text-white hover:text-[#C9A95C]"
        }`}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div className="border-b border-white/15">
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={`flex w-full items-center justify-between py-4 font-body text-[14px] font-medium uppercase tracking-[0.06em] transition-colors ${
          expanded || isParentActive ? "text-[#C9A95C]" : "text-white hover:text-[#C9A95C]"
        }`}
      >
        {item.label}
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${
            expanded ? "rotate-180 text-[#C9A95C]" : "text-white/70"
          }`}
        />
      </button>
      <div
        id={panelId}
        className={`overflow-hidden transition-all duration-300 ease-out ${
          expanded ? "max-h-60 pb-3" : "max-h-0"
        }`}
      >
        {item.children.map((child) => (
          <Link
            key={child.href}
            to={child.href}
            onClick={onClose}
            aria-current={location.pathname === child.href ? "page" : undefined}
            className={`block py-2.5 pl-6 font-body text-[13px] tracking-[0.04em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A95C]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f2e24] ${
              location.pathname === child.href
                ? "text-[#C9A95C] font-medium"
                : "text-white/80 hover:text-[#C9A95C]"
            }`}
          >
            {child.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Navbar ─── */
const Navbar = ({ variant = "default" }: { variant?: "default" | "consultation" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const scrolled = useScrolled(50);
  const location = useLocation();
  const { t, lang, setLang } = useLanguage();
  const isConsultation = variant === "consultation";
  const isTransparent = isConsultation ? true : !scrolled;

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const NAV_LINKS: NavItem[] = [
    {
      label: t(translations.nav.about),
      href: "/about",
      children: [
        { label: "About", href: "/about" },
        { label: "Team", href: "/team" },
        { label: "Community Impact", href: "/community-impact" },
      ],
    },
    {
      label: "Services",
      href: "/work-with-us",
      children: [
        { label: "Work With Us", href: "/work-with-us" },
        { label: "Buy a Home", href: "/buy-a-home" },
        { label: "Sell Your Home", href: "/sell-your-home" },
        { label: "Interior Design", href: "/interior-design" },
        { label: "Cleaning", href: "/cleaning" },
        { label: "Carpentry", href: "/carpentry" },
        { label: "Painting", href: "/painting" },
      ],
    },
    {
      label: "Massachusetts",
      href: "/listings",
      children: [
        { label: "Investing", href: "/invest" },
        { label: "Properties", href: "/listings" },
      ],
    },
    {
      label: "Dominican Republic",
      href: "/dominican-republic",
      children: [
        { label: "Investing", href: "/dominican-republic" },
        { label: "Properties", href: "/dominican-republic-investing/properties" },
      ],
    },
    {
      label: "Resources",
      href: "/resources",
      children: [
        { label: "Guides", href: "/resources/guides" },
        { label: "Events", href: "/events" },
      ],
    },
  ];

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed ${isConsultation ? "top-0" : "top-9"} left-0 right-0 z-50 transition-all duration-300 ${
        isOpen
          ? "bg-[#0f2e24] border-b border-white/15"
          : isTransparent
          ? "bg-transparent"
          : "bg-black/90 backdrop-blur-md border-b border-white/10"
      }`}
    >
      {isConsultation ? (
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
      ) : null}
      <div className="mx-auto max-w-[1520px] px-6 md:px-10 lg:px-16 relative flex items-center h-[80px] md:h-[100px]">
        {/* ─ Logo (left-anchored) ─ */}
        <Link to="/" className="flex items-center shrink-0 relative z-10 mr-6 lg:mr-10">
          <img
            src={logo}
            alt="Hadassah Homes"
            className={`h-14 md:h-[88px] w-auto object-contain transition-all duration-500 brightness-0 invert drop-shadow-[0_1px_3px_rgba(0,0,0,0.3)]`}
            width={220}
            height={88}
            {...({ fetchpriority: "high" } as any)}
            decoding="sync"
          />
        </Link>

        {/* ─ Desktop Center Nav (absolute-centered in viewport) ─ */}
        {!isConsultation ? (
          <div className="hidden lg:flex items-center justify-center gap-7 xl:gap-8 absolute inset-0 pointer-events-none">
            <div className="flex items-center gap-7 xl:gap-8 pointer-events-auto">
              {NAV_LINKS.map((link) => {
                if (link.children) {
                  return (
                    <NavDropdown
                      key={link.label}
                      item={link}
                      isTransparent={isTransparent}
                    />
                  );
                }
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`relative font-body text-[13px] font-medium uppercase tracking-[0.07em] transition-colors duration-300 whitespace-nowrap ${
                      isActive ? "text-accent" : "text-white/90 hover:text-white"
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-accent rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* ─ Desktop CTA (right-anchored) ─ */}
        <div className="hidden lg:flex items-center shrink-0 ml-auto relative z-10">
          {isConsultation ? (
            <div className="flex items-center gap-5">
              <GoogleTranslate variant="dark" />
              <a
                href="tel:+17816323936"
                className="inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.1em] font-semibold px-7 py-3 rounded-sm bg-accent text-accent-foreground transition-all duration-300 whitespace-nowrap hover:bg-accent/90 hover:-translate-y-[1px]"
              >
                Call Now
              </a>
            </div>
          ) : (
            <Link
              to="/consultation"
              className={`group inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.1em] font-semibold px-7 py-3 rounded-sm transition-all duration-300 whitespace-nowrap ${
                isTransparent
                  ? "bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-[0_8px_24px_-4px_hsl(var(--accent)/0.4)]"
                  : "bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-[0_8px_24px_-4px_hsl(var(--accent)/0.3)]"
              } hover:-translate-y-[1px]`}
            >
              Book a Consultation
              <ArrowRight
                size={13}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>
          )}
        </div>

        {/* ─ Mobile: CTA + Toggle ─ */}
        <div className="lg:hidden flex items-center gap-3 ml-auto">
          {isConsultation ? (
            <>
              <GoogleTranslate variant="dark" />
              <a
                href="tel:+17816323936"
                className="font-body text-[11px] uppercase tracking-[0.08em] font-semibold px-5 py-2.5 rounded-sm bg-[#C9A95C] text-white transition-all hover:bg-[#b89a50]"
              >
                Call Now
              </a>
            </>
          ) : (
            <>
              <Link
                to="/consultation"
                className="font-body text-[11px] uppercase tracking-[0.08em] font-semibold px-5 py-2.5 rounded-sm bg-[#C9A95C] text-white transition-all hover:bg-[#b89a50]"
              >
                Book Now
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 rounded-none transition-colors ${
                  isOpen
                    ? "text-white bg-white/10 hover:bg-white/15 border border-white/10"
                    : "text-white hover:bg-white/10"
                }`}
                aria-label="Toggle menu"
                aria-expanded={isOpen}
              >
                {isOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ─ Mobile Menu Overlay ─ */}
      {!isConsultation ? (
        <div
          className={`lg:hidden fixed inset-0 top-[116px] bg-[#0a1f18] transition-opacity duration-300 z-[90] ${
            isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsOpen(false)}
        />
      ) : null}

      {/* ─ Mobile Menu Panel ─ */}
      {!isConsultation ? (
        <div
          className={`lg:hidden fixed left-0 right-0 top-[116px] bottom-0 h-[calc(100vh-116px)] w-full border-b border-white/15 bg-[#0f2e24] shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-y-auto transition-all duration-400 ease-out z-[100] ${
            isOpen
              ? "opacity-100 pointer-events-auto translate-y-0"
              : "opacity-0 pointer-events-none -translate-y-2"
          }`}
        >
          <div className="px-6 py-5">
            {NAV_LINKS.map((link) => (
              <MobileAccordion
                key={link.label || link.href}
                item={link}
                onClose={() => setIsOpen(false)}
              />
            ))}

            {/* Mobile language switcher */}
            <div className="mt-6 pt-5 border-t border-white/15">
              <p className="font-body text-[#C9A95C] uppercase tracking-[0.18em] text-xs mb-3">
                Language
              </p>
              <GoogleTranslate variant="dark" />
            </div>

            {/* Mobile CTA */}
            <div className="mt-6 mb-2">
              <Link
                to="/consultation"
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center justify-center gap-2.5 rounded-sm bg-[#C9A95C] py-4 font-body text-[13px] font-semibold uppercase tracking-[0.08em] text-white transition-all hover:bg-[#b89a50] hover:shadow-[0_4px_16px_-4px_rgba(201,169,92,0.35)]"
              >
                Book a Consultation
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
};

export default Navbar;
